// @flow
import Url from 'url-parse'
import { matchPath } from 'react-router-dom'
import { createBrowserHistory, type BrowserHistory } from 'history'
import type { Unregister, Location } from 'history'
import type { RouterHistory } from 'react-router-dom'
import type { AppIdentity, App, Bootstrap } from 'core/bootstrap/types'
import type { AssetsManifest } from 'core/types'
import { APP } from 'core/constants/app'
import { SERVICES } from 'core/constants/services'
import { ioc } from 'core/ioc'
import { closeSidebar } from '../../features/menu/model/events'
import type { Updater } from '../updater'
import type { Location as AppLocation } from './types'
import { historyBlockers } from './blockers'
import { $locations } from './model/store'
import { pushLocation } from './model/events'
import { changeToLoginRoute } from './hooks/changeToLoginRoute'
import { logger as HistoryLogger } from './logger'

type AppListener = (location: Location) => void
export type AppHistory = {| history: RouterHistory, listener: AppListener, unlistenHistory: Unregister |}

const HOOKS = [changeToLoginRoute]
const MATCHED_APP_SORT_ORDER = ['market', 'user', 'elementary', 'wiki', 'lms', 'content_page']

export class HistoryService {
  historyCached: AppHistory

  syncStore: Array<AppLocation>

  history: BrowserHistory

  bootstrap: Bootstrap

  constructor() {
    const url = new URL(window.location.toString())

    pushLocation({ url })

    $locations.updates.watch((state) => {
      this.syncStore = state
    })

    this.syncStore = $locations.getState()

    this.createHistory()
    this.applyHistoryBlockers()

    this.bootstrap = window.$bootstrap
  }

  createHistory = (): BrowserHistory => {
    const _history: BrowserHistory = this.history || createBrowserHistory()

    this.history = _history

    return this.history
  }

  applyHistoryBlockers = () => {
    historyBlockers.forEach(({ blocker, shouldBlockerBeUsed }) => {
      if (shouldBlockerBeUsed()) {
        this.history.block(blocker)
      }
    })
  }

  /**
   * Возвращает все роуты модуля, собранные на этапе сборки
   * @param {AppIdentity} module имя модуля
   */
  getAppRoutes = (appModule: AppIdentity): string[] => {
    const { MODULE_ROUTES = [] } = process.env

    if (!Array.isArray(MODULE_ROUTES)) return []

    const module = MODULE_ROUTES.find((item) => item.name === appModule)

    return module && module.routes ? module.routes : []
  }

  /**
   * Возвращает все роуты всех модулей, собранные на этапе сборки
   */
  getAllRoutes = (): string[] => {
    const { MODULE_ROUTES = [] } = process.env

    if (!Array.isArray(MODULE_ROUTES)) return []
    ;(MODULE_ROUTES: Array<{ routes: Array<string> }>)

    return MODULE_ROUTES.flatMap((item) => item.routes || undefined)
  }

  createAppListener(): AppListener {
    HistoryLogger.debug('Create app history listener')

    // eslint-disable-next-line func-names
    const listener = (location: Location) => {
      HistoryLogger.debug('Trying change location to:', location)

      const currentApp = this.bootstrap.getCurrentApp()

      const paths = currentApp.name === APP.LMS ? this.getAllRoutes() : this.getAppRoutes(currentApp.name)

      HistoryLogger.debug('Current app', currentApp)
      HistoryLogger.debug('Current app paths', paths)

      const { pathname, search } = location
      const { MODULE_ROUTES = [] } = process.env

      pushLocation({ url: new URL(`${window.location.origin}${pathname}${search}`) })

      if (!Array.isArray(MODULE_ROUTES)) {
        this.moveWithReload(location)

        return
      }

      HOOKS.forEach((hook) => {
        hook(location, this.getState.bind(this))
      })

      const matchedApps: App[] = MODULE_ROUTES.filter((item) => {
        if (!item.routes) return false

        return matchPath(pathname, { path: item.routes, exact: item.name !== APP.CONTENT_PAGE })
      }).sort((a, b) => {
        return MATCHED_APP_SORT_ORDER.indexOf(a.name) - MATCHED_APP_SORT_ORDER.indexOf(b.name)
      })

      HistoryLogger.debug('Current app: ', currentApp.name)
      HistoryLogger.debug('Trying change location. Matched apps: ', matchedApps)

      const matchedApp = matchedApps[0]

      if (matchedApp) this.moveToAnotherApp(location, matchedApp)
      else this.moveWithReload(location)
    }

    return listener
  }

  createListener = (): AppHistory => {
    if (this.historyCached) return this.historyCached

    const listener = this.createAppListener()

    const unlistenHistory = this.history.listen(listener)

    if (!window.$history) window.$history = []

    window.$history = [...window.$history, listener]

    // flowlint-next-line unclear-type:off
    this.historyCached = { history: ((this.history: any): RouterHistory), listener, unlistenHistory }

    return this.historyCached
  }

  getState(): Array<AppLocation> {
    return this.syncStore
  }

  getHistory(): BrowserHistory {
    return this.history
  }

  /**
   * Делает переход на роут с перезагрузкой страницы или посредством загрузки ассетов апки
   * @param {Location} location
   * @param {App} matchedApp
   */
  moveToAnotherApp = async (location: Location, matchedApp: App): Promise<void> => {
    const updater: Updater = ioc.container[SERVICES.UPDATER]

    // коммитим что произошел переход на другую апку
    updater.commitCrossAppChangeLocation()

    // получаем манифест(если был загружен ранее, достаем из кэша)
    const manifest = await updater.getAppManifest()

    if (this.bootstrap.appIsRegistered(matchedApp.name)) {
      this.moveToRegisteredApp(matchedApp)
    } else {
      this.moveToUnregisteredApp(matchedApp, location, manifest)
    }
  }

  /**
   * Переход к зарегистрированному ранее приложению
   * @param {App} matchedApp
   */
  moveToRegisteredApp = (matchedApp: App): void => {
    const current = this.bootstrap.getCurrentApp()

    if (current.name === matchedApp.name) {
      HistoryLogger.debug('App is registered, prevent moveTo')

      return
    }

    HistoryLogger.debug('Force start app')

    if (this.bootstrap.unmountModule() === true) {
      this.bootstrap.setCurrentApp(matchedApp)
      this.bootstrap.start(matchedApp.name)
    } else {
      throw new Error("Can't unmount previous app")
    }
  }

  /**
   * Переход к незарегистрированному ранее приложению с загрузской ассетов из манифеста
   * @param {App} matchedApp
   */
  moveToUnregisteredApp = async (matchedApp: App, location: Location, manifest: AssetsManifest): Promise<void> => {
    this.bootstrap.showPreloader()

    try {
      const loadList = await this.bootstrap.loadAssets(matchedApp.name, manifest)

      if (loadList.every((loaded) => loaded === true)) {
        HistoryLogger.debug('All app assets have been loaded')
        HistoryLogger.debug('History change with cross-app transition was succesful')
        // do not start the app as it will start on its own index file
      } else {
        HistoryLogger.warn('Some assets can not be loaded -> window.reload')
        this.moveWithReload(location)
      }
    } catch (error) {
      HistoryLogger.error('Could not load app assets -> window.reload', error)
      this.moveWithReload(location)
    }
  }

  /**
   * Change location with window reload
   */
  moveWithReload = (location: Location) => {
    const reqUrl = new Url(location.pathname, true)

    window.location = `${reqUrl.toString()}${location.search || ''}`
  }

  // eslint-disable-next-line class-methods-use-this
  afterStartNewAppInRuntime(_matchedApp: App) {
    closeSidebar()
  }
}
